import React from 'react';
import { ClientStatus } from '../types/clientStatus';
import { clientStatusApi } from '../services/clientStatusApi';
import UserContext from '../context/userContext';
import { ContractType } from '../clientContracts/contract/entities';
import {
    GetContractResponse,
    GetNeedsToAcceptContractResponse,
} from '../clientContracts/contract/contractDTO';
import { AmbassadorHub } from '../types/ambassadorHub';

export const useClientStatus = (initialStatus: ClientStatus) => {
    const user = React.useContext(UserContext);
    const [status, setStatus] = React.useState(initialStatus);
    const [hubs, setHubs] = React.useState<AmbassadorHub[]>([]);
    const [isOnboardingTasksCompleted, setIsOnboardingTasksCompleted] = React.useState(false);
    const [needsToAcceptTerms, setNeedsToAcceptTerms] = React.useState<
        GetNeedsToAcceptContractResponse | undefined
    >(undefined);

    const [clientTerms, setClientTerms] = React.useState<GetContractResponse | undefined>(
        undefined,
    );
    const updateStatus = React.useCallback((updates: Partial<ClientStatus>) => {
        setStatus((prev) => ({ ...prev, ...updates }));
    }, []);

    const getClientOnboardingStatus = React.useCallback(async () => {
        if (!user.activeClient?.id) return;

        try {
            // For all clients, fetch these resources
            const [hubResponse, termsResponse] = await Promise.all([
                clientStatusApi.getHubs(),
                clientStatusApi.getTerms(ContractType.TERMS_AND_CONDITIONS),
            ]);

            setHubs(hubResponse?.payload ?? []);
            setClientTerms(termsResponse?.payload);

            const needsToAcceptTermsResponse = await clientStatusApi.getNeedsToAcceptTerms();
            setNeedsToAcceptTerms(needsToAcceptTermsResponse?.payload);

            if (user.activeClient.isLegacyClient) {
                // For legacy clients, set fixed status values
                setStatus({
                    isBankAccountConnected: true,
                    isEmailConnected: true,
                    hasHubSetup: true,
                });
                setIsOnboardingTasksCompleted(true);
            } else {
                // Only for non-legacy clients, fetch additional resources
                const emailProvidersResponse = await clientStatusApi.getEmailProviders();

                const emailProviders = emailProvidersResponse?.payload ?? [];
                const processedEmailProviders = emailProviders.filter(
                    (mp) => mp.provider !== 'sendgrid' || mp.forwardEmail,
                );

                const newStatus = {
                    isBankAccountConnected: Boolean(
                        user.activeClient?.modernTreasuryId || user.activeClient?.stripeId,
                    ),
                    isEmailConnected: processedEmailProviders?.length > 0,
                    hasHubSetup: hubResponse?.payload?.some((hub) => hub.isLive),
                };

                setStatus(newStatus);
                setIsOnboardingTasksCompleted(Object.values(newStatus).every(Boolean));
            }
        } catch (error) {
            console.error('Failed to fetch client onboarding status:', error);
        }
    }, [user.activeClient]);

    const acceptTerms = React.useCallback(async (contractVersionId: number) => {
        await clientStatusApi.acceptTerms(contractVersionId);
        const terms = await clientStatusApi.getTerms(ContractType.TERMS_AND_CONDITIONS);

        setNeedsToAcceptTerms(undefined);
        setClientTerms(terms?.payload);
    }, []);

    const declineTerms = React.useCallback(async (contractVersionId: number) => {
        await clientStatusApi.declineTerms(contractVersionId);

        setNeedsToAcceptTerms(undefined);
    }, []);

    React.useEffect(() => {
        getClientOnboardingStatus();
    }, [getClientOnboardingStatus]);

    return {
        status,
        updateStatus,
        isOnboardingTasksCompleted,
        acceptTerms,
        needsToAcceptTerms,
        hubs,
        declineTerms,
        clientTerms,
        getClientOnboardingStatus,
    };
};
